/**
 * Edit Medicine Page - Update existing medicine records
 * Mapped to backend Medicine.java entity with exact field names
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, ArrowLeft, Calendar, Pill } from 'lucide-react';
import { medicineApi, type Medicine } from '../../services/gaushala/api';
import { getLoggedInUserGaushalaId } from '../../utils/auth';

interface MedicineFormData {
  name: string;
  description: string;
  type: string;
  unit: string;
  quantity: number;
  expiryDate: string;
  manufacturer: string;
  batchNumber: string;
  purpose: string;
}

export default function EditMedicine() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Validation constraints
  const VALIDATION_RULES = {
    name: { min: 2, max: 100 },
    batchNumber: { min: 0, max: 50 },
    manufacturer: { min: 0, max: 100 },
    purpose: { min: 0, max: 500 },
    description: { min: 0, max: 1000 },
    quantity: { min: 1, max: 9999999 }
  };

  const [formData, setFormData] = useState<MedicineFormData>({
    name: '',
    description: '',
    type: '',
    unit: 'mg',
    quantity: 0,
    expiryDate: '',
    manufacturer: '',
    batchNumber: '',
    purpose: ''
  });

  const [errors, setErrors] = useState<Partial<MedicineFormData>>({});

  useEffect(() => {
    const fetchMedicine = async () => {
      if (!id) return;

      setLoading(true);
      try {
        const result = await medicineApi.getMedicineById(parseInt(id));

        if (result.success && result.data) {
          const medicine = result.data;
          // Convert ISO date string to YYYY-MM-DD format for date input
          const dateObj = new Date(medicine.expiryDate);
          const formattedDate = dateObj.toISOString().split('T')[0];

          setFormData({
            name: medicine.name,
            description: medicine.description || '',
            type: medicine.dosage,
            unit: medicine.unit,
            quantity: medicine.quantity,
            expiryDate: formattedDate,
            manufacturer: medicine.manufacturer || '',
            batchNumber: medicine.batchNumber || '',
            purpose: medicine.purpose || ''
          });
        } else {
          setMessage({
            type: 'error',
            text: result.error || 'Failed to load medicine record.'
          });
        }
      } catch (error) {
        console.error('Error fetching medicine record:', error);
        setMessage({
          type: 'error',
          text: 'Failed to load medicine record.'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchMedicine();
  }, [id]);

  const handleInputChange = (field: keyof MedicineFormData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof MedicineFormData, string>> = {};

    // Medicine Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Medicine Name is required';
    } else if (formData.name.trim().length < VALIDATION_RULES.name.min) {
      newErrors.name = `Medicine Name must be at least ${VALIDATION_RULES.name.min} characters`;
    } else if (formData.name.length > VALIDATION_RULES.name.max) {
      newErrors.name = `Medicine Name cannot exceed ${VALIDATION_RULES.name.max} characters`;
    }

    // Medicine Type validation
    if (!formData.type.trim()) newErrors.type = 'Medicine Type is required';

    // Unit validation
    if (!formData.unit.trim()) newErrors.unit = 'Unit is required';

    // Quantity validation
    if (formData.quantity < VALIDATION_RULES.quantity.min) {
      newErrors.quantity = `Quantity must be at least ${VALIDATION_RULES.quantity.min}`;
    } else if (formData.quantity > VALIDATION_RULES.quantity.max) {
      newErrors.quantity = `Quantity cannot exceed ${VALIDATION_RULES.quantity.max}`;
    } else if (formData.quantity <= 0) {
      newErrors.quantity = 'Quantity must be greater than 0';
    }

    // Batch Number validation (optional field, but has max length)
    if (formData.batchNumber && formData.batchNumber.length > VALIDATION_RULES.batchNumber.max) {
      newErrors.batchNumber = `Batch Number cannot exceed ${VALIDATION_RULES.batchNumber.max} characters`;
    }

    // Manufacturer validation (optional field, but has max length)
    if (formData.manufacturer && formData.manufacturer.length > VALIDATION_RULES.manufacturer.max) {
      newErrors.manufacturer = `Manufacturer cannot exceed ${VALIDATION_RULES.manufacturer.max} characters`;
    }

    // Purpose validation (optional field, but has max length)
    if (formData.purpose && formData.purpose.length > VALIDATION_RULES.purpose.max) {
      newErrors.purpose = `Purpose cannot exceed ${VALIDATION_RULES.purpose.max} characters`;
    }

    // Description validation (optional field, but has max length)
    if (formData.description && formData.description.length > VALIDATION_RULES.description.max) {
      newErrors.description = `Description cannot exceed ${VALIDATION_RULES.description.max} characters`;
    }

    // Expiry Date validation
    if (!formData.expiryDate.trim()) newErrors.expiryDate = 'Expiry Date is required';

    // Date validation - expiry date should be in the future
    if (formData.expiryDate) {
      const expiryDate = new Date(formData.expiryDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (expiryDate < today) {
        newErrors.expiryDate = 'Expiry Date should be in the future';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      setMessage({
        type: 'error',
        text: 'Please fill in all required fields and fix validation errors.'
      });
      return;
    }

    if (!id) {
      setMessage({
        type: 'error',
        text: 'Invalid medicine ID.'
      });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      // CRITICAL: Backend MedicineDTO requires specific field names and types
      // Backend expects: gaushalaId (Long), name (String), quantity (Long)
      // All fields must match backend validation exactly

      const medicineData = {
        gaushalaId: getLoggedInUserGaushalaId() || 1, // REQUIRED: Long type (from user context)
        name: formData.name.trim(), // REQUIRED: String, 2-255 chars
        quantity: Math.floor(formData.quantity), // REQUIRED: Long (integer, not float)
        description: formData.description.trim() || null, // OPTIONAL: String
        dosage: formData.type.trim() || null, // OPTIONAL: String (formerly type)
        unit: formData.unit.trim() || null, // OPTIONAL: String
        expiryDate: formData.expiryDate ? new Date(formData.expiryDate).toISOString() : null, // OPTIONAL: LocalDateTime ISO format
        manufacturer: formData.manufacturer.trim() || null, // OPTIONAL: String
        batchNumber: formData.batchNumber.trim() || null, // OPTIONAL: String
        purpose: formData.purpose.trim() || null // OPTIONAL: String
      };

      // Log the exact data being sent
      console.log('=== UPDATING MEDICINE DATA (ID: ' + id + ') ===');
      console.log('Medicine Data:', JSON.stringify(medicineData, null, 2));
      console.log('Field Types:');
      console.log('- gaushalaId:', typeof medicineData.gaushalaId, medicineData.gaushalaId);
      console.log('- name:', typeof medicineData.name, medicineData.name);
      console.log('- quantity:', typeof medicineData.quantity, medicineData.quantity);
      console.log('========================================');

      const result = await medicineApi.updateMedicine(parseInt(id), medicineData as any);

      if (result.success) {
        setMessage({
          type: 'success',
          text: 'Medicine record updated successfully!'
        });
        console.log('✓ Medicine updated successfully:', result.data);

        // Navigate back after delay
        setTimeout(() => {
          navigate('/gaushala/medicine');
        }, 2000);
      } else {
        // Display the actual error from backend
        const errorMessage = result.error || 'Failed to update medicine record.';
        console.error('✗ Backend error:', errorMessage);
        setMessage({
          type: 'error',
          text: errorMessage
        });
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error occurred';
      console.error('✗ Exception during update:', errorMsg, error);
      setMessage({
        type: 'error',
        text: `Error: ${errorMsg}`
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/gaushala/medicine');
  };

  const InputField = ({
    label,
    value,
    onChange,
    type = 'text',
    placeholder,
    required = false,
    error,
    className = '',
    ...props
  }: any) => (
    <div className={`space-y-2 ${className}`}>
      <label className="block text-sm font-medium text-gray-800">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(type === 'number' ? Number(e.target.value) : e.target.value)}
        placeholder={placeholder}
        className={`w-full px-3 py-2 bg-white border rounded-lg transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 ${
          error ? 'border-red-300 bg-red-50' : 'border-gray-300'
        }`}
        {...props}
      />
      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600">
          <span className="text-red-500">⚠</span>
          {error}
        </div>
      )}
    </div>
  );

  const SelectField = ({
    label,
    value,
    onChange,
    placeholder,
    required = false,
    error,
    options,
    className = ''
  }: any) => (
    <div className={`space-y-2 ${className}`}>
      <label className="block text-sm font-medium text-gray-800">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full px-3 py-2 bg-white border rounded-lg transition-all duration-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 ${
          error ? 'border-red-300 bg-red-50' : 'border-gray-300'
        }`}
      >
        <option value="">{placeholder}</option>
        {options.map((option: any) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600">
          <span className="text-red-500">⚠</span>
          {error}
        </div>
      )}
    </div>
  );

  const TextAreaField = ({
    label,
    value,
    onChange,
    placeholder,
    required = false,
    error,
    rows = 3,
    className = ''
  }: any) => (
    <div className={`space-y-2 ${className}`}>
      <label className="block text-sm font-medium text-gray-800">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        className={`w-full px-3 py-2 bg-white border rounded-lg transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 resize-vertical ${
          error ? 'border-red-300 bg-red-50' : 'border-gray-300'
        }`}
      />
      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600">
          <span className="text-red-500">⚠</span>
          {error}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-gray-500">Loading medicine record...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Edit Medicine</h1>
          <p className="text-gray-600 mt-1">Update medicine inventory record #{id}</p>
        </div>
        <button
          onClick={handleCancel}
          className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg border ${
          message.type === 'success'
            ? 'bg-green-50 border-green-200 text-green-800'
            : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          <div className="flex items-center gap-2">
            {message.type === 'success' ? (
              <span className="text-green-500">✓</span>
            ) : (
              <span className="text-red-500">⚠</span>
            )}
            {message.text}
          </div>
        </div>
      )}

      {/* Main Form */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-3">
            <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Pill className="h-5 w-5 text-blue-600" />
            </div>
            Edit Medicine Information
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Medicine Name */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-800">
                Medicine Name <span className="text-red-500">*</span>
                <span className="text-xs text-gray-500 font-normal ml-2">({formData.name.length}/{VALIDATION_RULES.name.max})</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => {
                  if (e.target.value.length <= VALIDATION_RULES.name.max) {
                    handleInputChange('name', e.target.value);
                  }
                }}
                maxLength={VALIDATION_RULES.name.max}
                placeholder="Enter medicine name (min 2 characters)"
                className={`w-full px-3 py-2 bg-white border rounded-lg transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 ${
                  errors.name ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
              />
              {errors.name && (
                <div className="flex items-center gap-2 text-sm text-red-600">
                  <span className="text-red-500">⚠</span>
                  {errors.name}
                </div>
              )}
            </div>

            {/* Batch Number */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-800">
                Batch Number
                <span className="text-xs text-gray-500 font-normal ml-2">({formData.batchNumber.length}/{VALIDATION_RULES.batchNumber.max})</span>
              </label>
              <input
                type="text"
                value={formData.batchNumber}
                onChange={(e) => {
                  if (e.target.value.length <= VALIDATION_RULES.batchNumber.max) {
                    handleInputChange('batchNumber', e.target.value);
                  }
                }}
                maxLength={VALIDATION_RULES.batchNumber.max}
                placeholder="Enter batch number (optional)"
                className={`w-full px-3 py-2 bg-white border rounded-lg transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 ${
                  errors.batchNumber ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
              />
              {errors.batchNumber && (
                <div className="flex items-center gap-2 text-sm text-red-600">
                  <span className="text-red-500">⚠</span>
                  {errors.batchNumber}
                </div>
              )}
            </div>

            {/* Medicine Type */}
            <SelectField
              label="Medicine Type"
              value={formData.type}
              onChange={(value: string) => handleInputChange('type', value)}
              placeholder="Select medicine type"
              required
              error={errors.type}
              options={[
                { value: 'Tablets', label: 'Tablets' },
                { value: 'Capsules', label: 'Capsules' },
                { value: 'Injections', label: 'Injections' },
                { value: 'Syrups', label: 'Syrups' },
                { value: 'Creams', label: 'Creams' },
                { value: 'Ointments', label: 'Ointments' },
                { value: 'Powders', label: 'Powders' },
                { value: 'Drops', label: 'Drops' },
                { value: 'Gels', label: 'Gels' },
                { value: 'Sprays', label: 'Sprays' }
              ]}
            />

            {/* Unit */}
            <SelectField
              label="Unit"
              value={formData.unit}
              onChange={(value: string) => handleInputChange('unit', value)}
              placeholder="Select unit"
              required
              error={errors.unit}
              options={[
                { value: 'mg', label: 'Milligrams (mg)' },
                { value: 'mcg', label: 'Micrograms (mcg)' },
                { value: 'g', label: 'Grams (g)' },
                { value: 'ml', label: 'Milliliters (ml)' },
                { value: 'l', label: 'Liters (l)' },
                { value: 'iu', label: 'International Units (IU)' },
                { value: 'mmol', label: 'Millimoles (mmol)' }
              ]}
            />

            {/* Quantity */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-800">
                Quantity <span className="text-red-500">*</span>
                <span className="text-xs text-gray-500 font-normal ml-2">(Whole numbers only, Max: {VALIDATION_RULES.quantity.max})</span>
              </label>
              <input
                type="number"
                min={VALIDATION_RULES.quantity.min}
                max={VALIDATION_RULES.quantity.max}
                step="1"
                value={formData.quantity}
                onChange={(e) => {
                  const value = Math.max(0, Math.floor(parseFloat(e.target.value) || 0));
                  if (value <= VALIDATION_RULES.quantity.max) {
                    handleInputChange('quantity', value);
                  }
                }}
                placeholder="Enter whole number quantity (1, 2, 3...)"
                className={`w-full px-3 py-2 bg-white border rounded-lg transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 ${
                  errors.quantity ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
              />
              {errors.quantity && (
                <div className="flex items-center gap-2 text-sm text-red-600">
                  <span className="text-red-500">⚠</span>
                  {errors.quantity}
                </div>
              )}
            </div>

            {/* Expiry Date */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-800">
                Expiry Date <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="date"
                  value={formData.expiryDate}
                  onChange={(e) => handleInputChange('expiryDate', e.target.value)}
                  className={`w-full px-3 py-2 bg-white border rounded-lg transition-all duration-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 ${
                    errors.expiryDate ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                />
                <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>
              {errors.expiryDate && (
                <div className="flex items-center gap-2 text-sm text-red-600">
                  <span className="text-red-500">⚠</span>
                  {errors.expiryDate}
                </div>
              )}
            </div>

            {/* Manufacturer */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-800">
                Manufacturer
                <span className="text-xs text-gray-500 font-normal ml-2">({formData.manufacturer.length}/{VALIDATION_RULES.manufacturer.max})</span>
              </label>
              <input
                type="text"
                value={formData.manufacturer}
                onChange={(e) => {
                  if (e.target.value.length <= VALIDATION_RULES.manufacturer.max) {
                    handleInputChange('manufacturer', e.target.value);
                  }
                }}
                maxLength={VALIDATION_RULES.manufacturer.max}
                placeholder="Enter manufacturer name (optional)"
                className={`w-full px-3 py-2 bg-white border rounded-lg transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 ${
                  errors.manufacturer ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
              />
              {errors.manufacturer && (
                <div className="flex items-center gap-2 text-sm text-red-600">
                  <span className="text-red-500">⚠</span>
                  {errors.manufacturer}
                </div>
              )}
            </div>

            {/* Purpose */}
            <div className="md:col-span-2 space-y-2">
              <label className="block text-sm font-medium text-gray-800">
                Purpose
                <span className="text-xs text-gray-500 font-normal ml-2">({formData.purpose.length}/{VALIDATION_RULES.purpose.max})</span>
              </label>
              <textarea
                value={formData.purpose}
                onChange={(e) => {
                  if (e.target.value.length <= VALIDATION_RULES.purpose.max) {
                    handleInputChange('purpose', e.target.value);
                  }
                }}
                maxLength={VALIDATION_RULES.purpose.max}
                placeholder="Enter purpose or indication for use (optional)"
                rows={2}
                className={`w-full px-3 py-2 bg-white border rounded-lg transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 resize-vertical ${
                  errors.purpose ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
              />
              {errors.purpose && (
                <div className="flex items-center gap-2 text-sm text-red-600">
                  <span className="text-red-500">⚠</span>
                  {errors.purpose}
                </div>
              )}
            </div>

            {/* Description */}
            <div className="md:col-span-2 space-y-2">
              <label className="block text-sm font-medium text-gray-800">
                Description
                <span className="text-xs text-gray-500 font-normal ml-2">({formData.description.length}/{VALIDATION_RULES.description.max})</span>
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => {
                  if (e.target.value.length <= VALIDATION_RULES.description.max) {
                    handleInputChange('description', e.target.value);
                  }
                }}
                maxLength={VALIDATION_RULES.description.max}
                placeholder="Enter additional details about the medicine (optional)"
                rows={3}
                className={`w-full px-3 py-2 bg-white border rounded-lg transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 resize-vertical ${
                  errors.description ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
              />
              {errors.description && (
                <div className="flex items-center gap-2 text-sm text-red-600">
                  <span className="text-red-500">⚠</span>
                  {errors.description}
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-start gap-4 mt-8 pt-6 border-t border-gray-100">
            <button
              type="button"
              onClick={handleCancel}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Save className="h-4 w-4" />
              {isLoading ? 'Updating...' : 'Update Medicine'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
